import React, { useEffect, useState } from "react";

import OpenExternalIcon from "@uui/ArrowUpRight";
import { isEmpty } from "lodash/fp";

import { CHANNEL_OPTS_FOR_TOPIC_PROVIDER } from "@app/analytics/constants/topics";
import type { TopicCitationGroup } from "@app/analytics/TopicReport/types";
import type { TopicEntity } from "@app/analytics/types";
import { getRelativeDisplayDate } from "@app/analytics/utils";
import { truncateAlongWordBoundary } from "@app/common/utils";
import * as TopicUtils from "@app/common/utils/topicUtils";

import Link from "@rui/Link";
import { Column, Row } from "@rui/Stack";
import { Bold, Img } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

import {
  Accordion,
  AccordionDetails,
  AccordionHeader,
  CitationText,
  CitationWrapper,
  DetailText,
  ImgContainer,
  ThreadHeading,
} from "./styles";

const Citation = ({
  citation,
  spotlight,
}: {
  citation: TopicEntity;
  spotlight: boolean;
}) => {
  const channelName = TopicUtils.getProviderDisplayName(citation.provider);
  const truncatedText = truncateAlongWordBoundary(citation.text, 200);

  return (
    <CitationWrapper
      spacing={1}
      className={spotlight ? "spotlight" : undefined}
    >
      <Row variant="apart" spacing={1}>
        <Row variant="left" spacing={1} overflow="hidden">
          {citation.author && (
            <CitationText noWrap>
              <Bold>{citation.author.displayName}</Bold>
            </CitationText>
          )}
          <TooltipWrapper
            title={citation.endTime.toFormat("DD h:mm a")}
            enterDelay={2000}
          >
            <CitationText color="shades.neutral.500" noWrap>
              {getRelativeDisplayDate(citation.endTime)}
            </CitationText>
          </TooltipWrapper>
        </Row>
        {channelName && citation.providerLink && (
          <Link href={citation.providerLink} fontWeight={400}>
            <Row variant="right" spacing={0.5}>
              <CitationText className="open-citation__link-label" noWrap>
                Open in {channelName}
              </CitationText>
              <OpenExternalIcon
                sx={{ fontSize: "20px", color: "shades.secondary.400" }}
              />
            </Row>
          </Link>
        )}
      </Row>
      <CitationText>{truncatedText}</CitationText>
    </CitationWrapper>
  );
};

const ThreadCitation = ({
  contributors,
  groupLink,
  provider,
}: TopicCitationGroup) => {
  const channelName = TopicUtils.getProviderDisplayName(provider);

  const showLink = Boolean(channelName && groupLink);
  return (
    <CitationWrapper spacing={0.5}>
      <CitationText>
        <Bold>Contributors:</Bold> {contributors.join(", ")}
      </CitationText>
      {showLink && (
        <DetailText>
          <Link href={groupLink} fontWeight={400}>
            Open in {channelName}
          </Link>
        </DetailText>
      )}
    </CitationWrapper>
  );
};

export interface CitationGroupProps extends TopicCitationGroup {
  forceExpand?: boolean;
  selectedEntityId?: TopicEntity["entityId"] | null;
}

const CitationGroup = (props: CitationGroupProps) => {
  const [expanded, setExpanded] = useState(false);

  const { selectedEntityId, forceExpand = false, ...group } = props;
  const { citations, groupName, provider } = group;

  const providerLogo = TopicUtils.getProviderLogoSrc(provider);

  const threadNamePrefix =
    CHANNEL_OPTS_FOR_TOPIC_PROVIDER[provider]?.channelPrefix ?? "";
  const handleToggleExpanded = () => setExpanded((e) => !e);
  const shouldForceExpand = !expanded && forceExpand;

  useEffect(() => {
    if (!shouldForceExpand) return;
    setExpanded(true);
  }, [shouldForceExpand]);

  if (isEmpty(citations) && isEmpty(group.contributors)) return null;

  return (
    <Accordion expanded={expanded}>
      <AccordionHeader onClick={handleToggleExpanded}>
        {providerLogo && (
          <ImgContainer>
            <Img height="12px" src={providerLogo} />
          </ImgContainer>
        )}
        <ThreadHeading noWrap title={groupName}>
          {threadNamePrefix}
          {groupName}
          {citations.length > 0 ? ` (${citations.length})` : ""}
        </ThreadHeading>
      </AccordionHeader>
      <AccordionDetails>
        {isEmpty(citations) ? (
          <ThreadCitation {...group} />
        ) : (
          <Column spacing={2}>
            {citations.map((citation) => {
              const spotlight =
                selectedEntityId != null &&
                citation.parentEntityId === selectedEntityId;
              return (
                <Citation
                  key={citation.entityId}
                  citation={citation}
                  spotlight={spotlight}
                />
              );
            })}
          </Column>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default CitationGroup;
